import { useStore } from "@nanostores/react"
import { supabase } from "./supabase"
import { useQuery } from "@tanstack/react-query"
import { useEffect, useMemo, useState } from "react"

import { getNativeArPrice } from "./price-oracle-api"
import { getBalance, getNativeArBalance, TokenInfo } from "./token-api"
import ENV from "@/env"
import { useTokenInfos } from "@/hooks/use-token-info"
import { AO, L1_AR, REFRESH_INTERVAL } from "@/settings"
import { $allPools } from "@/stores/pools-store"
import { parseBigIntAsNumber } from "@/utils/format"
import { Tag } from "./ao"

export interface RichTokenInfo extends TokenInfo {
  id: string
  balance: bigint
  value?: number
  price?: number
  hasPool?: boolean
}

export function useAllPools() {
  const pools = useStore($allPools)
  const loadingPools = pools.length === 0

  return [pools, loadingPools] as const
}

export function useTokenIds() {
  const [pools] = useAllPools()

  const tokenIds = useMemo<string[]>(() => {
    const set = new Set<string>()

    set.add(L1_AR)
    set.add(AO)

    for (const pool of pools) {
      set.add(pool.baseToken)
      set.add(pool.quoteToken)
    }

    return Array.from(set)
  }, [pools])

  return tokenIds
}

export function useAoPrice() {
  return useQuery({
    queryKey: ["ao_price"],
    queryFn: async () => {
      try {
        const { data, error } = await supabase.functions.invoke('hopper', {
          body: {
            baseToken: AO,
            quoteToken: "USD",
            amountIn: "1000000",
            priceOnly: true,
          },
        })

        if (error) {
          throw new Error(`Supabase function error: ${error.message}`)
        }

        if (!data) {
          throw new Error("No response from hopper")
        }

        // Handle both new nested structure (data.Prices) and old structure (data.price or data)
        if (data.Prices && data.Prices[AO]) {
          return data.Prices[AO].price || 0
        }

        // Fallback to old structure for backward compatibility
        return data.Price || data || 0
      } catch (error) {
        console.error("Failed to fetch ao price", error)
        throw error
      }
    },
  })
}

export function useTokenPrices() {
  const [pools, loadingPools] = useAllPools()
  const tokenIds = useTokenIds()

  // fetch prices
  const { data: prices, isFetched: isPricesFetched } = useQuery<
    Record<string, { price: number }> | undefined
  >({
    queryKey: ["token_prices", tokenIds],
    enabled: !loadingPools,
    queryFn: async () => {
      if (tokenIds === null || tokenIds.length === 0 || loadingPools) {
        throw new Error("Invalid params")
      }

      try {
        const tokenIdsAndAO = [...tokenIds, AO]
        const { data, error } = await supabase.functions.invoke('hopper', {
          body: {
            batch: tokenIdsAndAO,
            priceOnly: true
          }
        })

        if (error) {
          throw new Error(`Supabase function error: ${error.message}`)
        }

        if (!data) {
          throw new Error("No response from hopper")
        }

        await new Promise((resolve) => setTimeout(resolve, 1000)) // wait for 1 second

        const arPrice = await getNativeArPrice()

        const prices = {
          ...(data.Prices || data),
          [L1_AR]: { price: arPrice }
        }
        console.log("onTokenPrices", prices)
        return prices
      } catch (error) {
        console.error("Failed to fetch prices", error)
        throw error
      }
    },
  })

  const loadingPrices = !isPricesFetched

  return [prices, loadingPrices] as const
}

export function usePortfolio(entityId: string): [RichTokenInfo[] | null] {
  const [pools, loadingPools] = useAllPools()
  const tokenIds = useTokenIds()

  // tokens
  const [tokens, loadingTokenInfos] = useTokenInfos(tokenIds)

  // fetch balances
  const [loadingBalances, setLoadingBalances] = useState(true)
  const [balances, setBalances] = useState<Record<string, bigint>>({})

  useEffect(() => {
    setBalances({})
    setLoadingBalances(true)
  }, [entityId])

  const { data: tokensWithBalance = [] } = useQuery({
    queryKey: ["token_balances", tokenIds, loadingPools, entityId],
    enabled: !!entityId && !loadingPools,
    queryFn: async () => {
      if (tokenIds.length === 0 || loadingPools || !entityId) {
        throw new Error("Invalid params")
      }

      try {
        console.log("onTokenBalancesRequest", tokenIds)
        const res = await Promise.all(
          tokenIds.map(async (id) => {
            const balance =
              id === L1_AR
                ? await getNativeArBalance(entityId)
                : await getBalance(id, entityId, REFRESH_INTERVAL).catch(() => 0n)

            setBalances((prev) => ({
              ...prev,
              [id]: balance,
            }))

            if (balance > 0n) {
              return id
            }

            return null
          }),
        )
        setLoadingBalances(false)
        const result = res.filter((x) => x !== null) as unknown[] as string[]
        return result
      } catch (error) {
        console.error("Failed to fetch balances", error)
        setLoadingBalances(false)
      }
    },
  })

  useQuery({
    queryKey: ["existing_balances", tokensWithBalance, loadingPools, entityId],
    enabled: !!entityId && !loadingPools,
    queryFn: async () => {
      if (tokensWithBalance.length === 0 || loadingPools || !entityId) {
        throw new Error("Invalid params")
      }

      try {
        console.log("onTokenBalancesRefreshRequest", tokensWithBalance)
        await Promise.all([
          tokensWithBalance.forEach(async (id) => {
            const balance =
              id === L1_AR ? await getNativeArBalance(entityId) : await getBalance(id, entityId)

            setBalances((prev) => ({
              ...prev,
              [id]: balance,
            }))
          }),
        ])
      } catch (error) {
        console.error("Failed to fetch balances", error)
      }

      return null
    },
  })

  const [prices = {}, loadingPrices] = useTokenPrices()

  // tokens with balances & prices
  const richTokens = useMemo(() => {
    if (!tokens) return null

    const list = tokens.map((token) => {
      const balance = balances[token.id]
      const price = prices[token.id]?.price
      const hasPool = token.id in prices
      const value =
        (price || 0) * Number(parseBigIntAsNumber(balance, token.denomination, token.denomination))

      return {
        ...token,
        balance,
        price,
        value,
        hasPool,
      }
    })

    // sort by value, otherwise sort by price
    return list.sort((a, b) => {
      if (a.value && !b.value) return -1
      if (!a.value && b.value) return 1
      if (a.value && b.value) return a.value > b.value ? -1 : 1

      return a.price > b.price ? -1 : 1
    })
  }, [tokens, balances, prices, entityId])

  // manage loading state
  const loading = useMemo(
    () =>
      !!loadingPools ||
      !!loadingTokenInfos ||
      !!loadingPrices ||
      (!!loadingBalances && Object.keys(balances).length === 0),
    [loadingPools, loadingTokenInfos, loadingBalances, balances, loadingPrices],
  )

  return [loading ? null : richTokens]
}

export function useLpBalance(entityId: string, poolId: string) {
  const { data: initialLpBalance } = useQuery({
    queryKey: ["lp_balance", entityId, poolId],
    enabled: !!entityId && !!poolId,
    queryFn: () => {
      if (!entityId || !poolId) throw new Error("No active address")
      return getBalance(poolId, entityId, REFRESH_INTERVAL).catch((error) => {
        // console.error("onLpPositionError", error)
        console.log("onLpPositionError", poolId)
        return 0n
      })
    },
  })

  const [firstCall, setFirstCall] = useState(true)

  const { data: lpBalance } = useQuery({
    queryKey: ["existing_lp_balance", entityId, poolId],
    enabled: !!entityId && initialLpBalance !== undefined && initialLpBalance > 0n,
    queryFn: async () => {
      if (!entityId || !poolId) throw new Error("No active address")
      if (firstCall) {
        setFirstCall(false)
        return initialLpBalance
      }
      console.log("onLpBalanceRefreshRequest", poolId, initialLpBalance)
      return getBalance(poolId, entityId, REFRESH_INTERVAL).catch((error) => {
        // console.error("onLpPositionError", error)
        console.log("onLpPositionError", poolId)
        return 0n
      })
    },
  })

  return lpBalance || initialLpBalance
}
